class HeroSlider extends HTMLElement {
  constructor() {
    super();
    this.defaultSpeed = 600;

    this.mountSlider();

    window.addEventListener('shopify:block:select', e => {
      const selectedSlideIndex = +e.target.dataset.index;
      this.slider.slideTo(selectedSlideIndex, 600);
    });
  }

  mountSlider() {
    // Create unique IDs for this carousel instance
    const carouselId = `carousel-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.setAttribute('data-carousel-id', carouselId);
    
    // Find navigation buttons - try multiple approaches
    let nextBtn = this.querySelector('.swiper-button--next');
    let prevBtn = this.querySelector('.swiper-button--prev');
    
    // If not found inside, try parent section
    if (!nextBtn || !prevBtn) {
      const section = this.closest('section') || this.closest('[data-section-id]');
      if (section) {
        nextBtn = nextBtn || section.querySelector('.swiper-button--next');
        prevBtn = prevBtn || section.querySelector('.swiper-button--prev');
      }
    }
    
    // Only assign to this carousel if buttons are not already assigned to another carousel
    if (nextBtn && !nextBtn.hasAttribute('data-carousel-id')) {
      nextBtn.setAttribute('data-carousel-id', carouselId);
      nextBtn.id = `next-${carouselId}`;
    }
    if (prevBtn && !prevBtn.hasAttribute('data-carousel-id')) {
      prevBtn.setAttribute('data-carousel-id', carouselId);
      prevBtn.id = `prev-${carouselId}`;
    }

    const autoplayEnabled = this.dataset.autoplay === 'true';
    const autoplayStartDelay = Number(this.dataset.autoplayStartDelay) || 600;
    const autoplayOptions = {
      delay: Number(this.dataset.autoplayInterval) || 5000,
      disableOnInteraction: false
    };

    this.slider = new Swiper(this, {
      effect: 'fade',
      rewind: true,
      slidesPerView: 1,
      // Keep first paint static; restore transition speed after first hero media is ready.
      speed: 0,
      followFinger: false,
      navigation: {
        nextEl: (nextBtn && nextBtn.getAttribute('data-carousel-id') === carouselId) ? `#next-${carouselId}` : null,
        prevEl: (prevBtn && prevBtn.getAttribute('data-carousel-id') === carouselId) ? `#prev-${carouselId}` : null
      },
      autoplay: autoplayEnabled ? autoplayOptions : false,
      on: {
        init: swiper => {
          this.handleSlideChange(swiper);
          this.prepareInitialRender(swiper, autoplayEnabled, autoplayStartDelay);
        },
        slideChange: this.handleSlideChange.bind(this)
      }
    });
  }

  prepareInitialRender(swiper, autoplayEnabled, autoplayStartDelay) {
    if (!swiper) return;

    if (autoplayEnabled && swiper.autoplay) {
      swiper.autoplay.stop();
    }

    this.waitForFirstSlideMedia(swiper).finally(() => {
      // Restore default transition behavior after first hero media has loaded.
      swiper.params.speed = this.defaultSpeed;
      this.classList.add('hero-slider--ready');

      if (autoplayEnabled && swiper.autoplay) {
        window.setTimeout(() => {
          swiper.autoplay.start();
        }, autoplayStartDelay);
      }
    });
  }

  waitForFirstSlideMedia(swiper) {
    return new Promise(resolve => {
      const activeSlide =
        swiper.slides && swiper.slides.length > 0
          ? swiper.slides[swiper.activeIndex]
          : this.querySelector('.hero__inner');

      if (!activeSlide) {
        resolve();
        return;
      }

      const mediaContainers = Array.from(activeSlide.querySelectorAll('.hero__media'));
      const visibleMedia =
        mediaContainers.find(media => window.getComputedStyle(media).display !== 'none') ||
        mediaContainers[0];

      if (!visibleMedia) {
        resolve();
        return;
      }

      const image = visibleMedia.querySelector('img');
      if (image) {
        if (image.complete && image.naturalWidth > 0) {
          resolve();
          return;
        }

        const onDone = () => resolve();
        image.addEventListener('load', onDone, { once: true });
        image.addEventListener('error', onDone, { once: true });
        return;
      }

      const video = visibleMedia.querySelector('video');
      if (video) {
        if (video.readyState >= 2) {
          resolve();
          return;
        }

        const onDone = () => resolve();
        video.addEventListener('loadeddata', onDone, { once: true });
        video.addEventListener('error', onDone, { once: true });
        return;
      }

      // External iframes don't provide a reliable lightweight readiness signal for LCP.
      resolve();
    });
  }

  handleSlideChange(swiper) {
    let headerInner = document.querySelector('.header__inner');
    let heroInners = document.querySelectorAll('.hero__inner');

    if (!headerInner || !heroInners) {
      return;
    }

    // change --transparent-header-menu-text-color value on document style attributes
    document.documentElement.style.setProperty(
      '--transparent-header-menu-text-color',
      heroInners[swiper.activeIndex].dataset.headerMenuTextColor
    );
  }
}

customElements.define('hero-slider', HeroSlider);
